import { useState } from "react";
import { uploadDoc } from "../api";
import { pollUntilComplete } from "@/utils/api/jobApi";
import { useProfileStore } from "../store/profileStore";
import { showNotification } from "@mantine/notifications";
import { FORM2ID } from "@/constants.ts";
import type { DocumentUserType } from "@pages/ClientProfile/types.ts";
import { emitDebugEvent } from "@utils/debug/eventIntegration";

interface UploadDocPayload {
  clientId: string;
  taxYear: number;
  userType: DocumentUserType;
  form: string;
  fileName: string;
  data: string;
}

export const useUploadDoc = () => {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    setOcrProcessingDocId,
    clearOcrProcessingDocId,
    setOCRJobStatus,
    documents,
    setDocuments,
    refetch,
    userId,
  } = useProfileStore();

  const uploadDocAndReturnJobInfo = async (
    payload: UploadDocPayload
  ): Promise<{ success: boolean; jobId?: string; docId?: string }> => {
    setIsUploading(true);
    setError(null);

    try {
      payload.form =
        payload.form in FORM2ID ? FORM2ID[payload.form] : payload.form;
      payload.form = payload.form.toLowerCase();
      const uploadResponse = await uploadDoc(payload);
      const jobId = uploadResponse?.jobId;
      const docId = uploadResponse?.docId;
      const newDoc = uploadResponse?.doc;

      if (!jobId || !docId) {
        throw new Error("Missing jobId or docId from response");
      }

      setOcrProcessingDocId(docId);

      // Add the newly uploaded document to the documents array immediately
      if (newDoc) {
        setDocuments([...documents, newDoc]);
      }

      // Emit debug event for successful upload
      const context: Record<string, string | number | boolean | null> = {
        fileName: payload.fileName?.substring(0, 100) || "unknown",
      };
      if (payload.form) context.formType = payload.form;
      if (payload.clientId) context.clientId = payload.clientId;
      if (payload.taxYear) context.taxYear = payload.taxYear.toString();
      emitDebugEvent("action", "DOCUMENT_UPLOADED", "web", context);

      return { success: true, jobId, docId };
    } catch (err) {
      console.error("Upload failed:", err);

      // Removed duplicate DOCUMENT_PROCESSING_FAILED event
      // This event is already emitted in useDocumentUpload.tsx (the modern hook)
      // Keeping DOCUMENT_UPLOADED here for legacy code path tracking

      setError("Failed to upload document");

      showNotification({
        title: "Upload failed",
        message: "Something went wrong during the upload. Please try again.",
        color: "red",
      });

      return { success: false };
    } finally {
      setIsUploading(false);
    }
  };

  const pollOCRJob = async (jobId: string, _docId: string): Promise<void> => {
    try {
      const jobStatus = await pollUntilComplete(jobId);
      setOCRJobStatus(jobStatus);

      if (jobStatus === "failed") {
        showNotification({
          title: "OCR Failed",
          message: "There was a problem processing your document.",
          color: "red",
        });
      } else if (jobStatus === "completed") {
        console.log(
          "[useUploadDoc] OCR completed, refetching profile to get updated data"
        );

        // Get document info for event context
        const document = documents.find((doc) => doc.docId === _docId);

        // Emit debug event for document processing completed
        emitDebugEvent("action", "DOCUMENT_PROCESSING_COMPLETED", "web", {
          fileName: document?.label?.substring(0, 100) || "unknown",
          jobId: jobId,
          clientId: userId || "unknown",
          formType: document?.key || "unknown",
        });

        // Refetch the entire profile to ensure we have:
        // - Updated document metadata
        // - New diagnostic errors
        // - Updated OCR state
        // - Updated OCR extractions with data
        await refetch();

        showNotification({
          title: "OCR Complete",
          message: "Your document was successfully processed.",
          color: "green",
        });
      }
    } catch (err) {
      console.error("OCR polling failed:", err);
      setOCRJobStatus("failed");

      showNotification({
        title: "OCR Timeout",
        message: "Document processing timed out or failed.",
        color: "red",
      });
    } finally {
      clearOcrProcessingDocId();
    }
  };

  return {
    uploadDocAndReturnJobInfo,
    pollOCRJob,
    isUploading,
    error,
  };
};

// Removed: React Query cache update functions
// Now we simply refetch() the profile after OCR completion
